<template>
  <v-container>
    <div class="articles-toolbar">
      <header class="articles-toolbar__header">
        <h2 class="title">News, Press Releases, Etc.</h2>
      </header>

      <v-text-field
        v-model="search"
        prepend-icon="search"
        label="search"
        class="articles-toolbar__search" />

      <v-select
        v-model="currentSort"
        :items="sortCriteria"
        label="sort by"
        class="sort-select"
        return-object
        disabled />
    </div>

    <v-scroll-y-transition
      group
      mode="out-in"
      class="articles">
      <h1
        v-if="articles.length === 0"
        key="noData"
        class="no-data">
        No Articles
      </h1>
      <router-link
        v-for="article in paginatedArticles"
        :key="article.slug"
        v-ripple
        :to="{
          name: 'article',
          params: {
            slug: article.slug
          }
        }"
        tag="div"
        :style="`background-image: url(${imageDir + article.coverImage})`"
        class="article">
        <h1
          v-if="article.title"
          class="article__title">
          {{ article.title }}
        </h1>
        <h2
          v-if="article.subtitle"
          class="article__subtitle">
          {{ article.subtitle }}
        </h2>
        <h3 class="article__date">{{ moment(article.dateCreated).format('MMMM Do YYYY') }}</h3>
      </router-link>
    </v-scroll-y-transition>

    <div class="load-more">
      <span class="load-more__page-details">
        Showing {{ pageItemCount }} of {{ filteredArticles.length }} articles
      </span>
      <v-progress-linear
        v-model="paginationProgress"
        height="2"
        background-color="accent"
        class="load-more__progress" />
      <v-btn
        outline
        color="primary"
        class="load-more__load-more-button"
        :disabled="filteredArticles.length === paginatedArticles.length"
        @click="loadNextPage">
        Load More
      </v-btn>
      <v-btn
        flat
        small
        class="load-more__go-to-top"
        @click="scrollToTop">
        go to top
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'AllArticles',
  data() {
    return {
      search: '',
      sortCriteria: [
        {
          text: 'Newest',
          value: 'dateCreated',
          direction: 'desc',
        },
      ],
      currentSort: {
        text: 'Newest',
        value: 'dateCreated',
        direction: 'desc',
      },
      pageNumber: 1,
      pageLimit: 8,
    };
  },
  computed: {
    ...mapState('articles', {
      articles: 'articles',
      imageDir: 'imageDir',
      loading: 'loading',
      errorMessage: 'errorMessage',
    }),
    sortedArticles() {
      let dir;
      if (this.currentSort.direction === 'asc') { dir = -1; }
      if (this.currentSort.direction === 'desc') { dir = 1; }

      return this.articles.map(x => x).sort((a, b) => {
        const sortVal = this.currentSort.value;
        if (a[sortVal] < b[sortVal]) return 1 * dir;
        if (a[sortVal] > b[sortVal]) return -1 * dir;
        return 0;
      });
    },
    filteredArticles() {
      const searchWords = this.search.toLowerCase().split(' ');
      return this.sortedArticles.filter((article) => {
        if (this.search !== '') {
          return searchWords.every(word => article.title.toLowerCase().includes(word));
        }
        return true;
      });
    },
    paginatedArticles() {
      return this.filteredArticles.filter((row, index) => {
        const limit = this.pageNumber * this.pageLimit;
        if (index < limit) return true;
        return false;
      });
    },
    paginationProgress() {
      return (
        ((this.pageNumber * this.pageLimit) / this.filteredArticles.length) * 100
      );
    },
    pageItemCount() {
      return this.pageNumber * this.pageLimit >= this.filteredArticles.length
        ? this.filteredArticles.length
        : this.pageNumber * this.pageLimit;
    },
  },
  created() {
    this.$store.dispatch('articles/getAllArticles');
    // initial sort: by date
    this.currentSort = {
      text: 'Newest',
      value: 'dateCreated',
      direction: 'desc',
    };
  },
  methods: {
    loadNextPage() {
      this.pageNumber++;
    },
    scrollToTop() {
      // https://www.w3schools.com/howto/howto_js_scroll_to_top.asp
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.articles-toolbar
  display: flex
  flex-flow: row wrap
  align-items: flex-end
  justify-content: space-between
  padding: $base-padding
  &__header
    display: flex
    align-items: center
    justify-content: center
    width: 100%
  &__search
    flex: 1 1 0

.sort-select
  flex: 0 1 27%
  margin-left: $base-padding*2


.no-data
  font-family: cursive
  color: $accent

.articles
  width: 100%
  display: flex
  flex-flow: row wrap
  align-items: stretch

.article
  margin: $base-padding
  position: relative
  min-height: 16rem
  display: flex
  flex: 1 1 40%
  min-width: 12rem
  flex-flow: column nowrap
  align-items: flex-start
  justify-content: center
  padding: $base-padding*2
  color: $secondary
  cursor: pointer
  transition: $base-transition
  border-radius: $base-border-radius

  background-position: center center
  background-size: cover
  background-repeat: no-repeat
  background-color: rgba(6,9,15, .8);
  background-blend-mode: multiply;
  &:hover
    background-color: $primary

  &__title,
  &__subtitle,
  &__date
    color: $white
    opacity: .95
    text-shadow: $base-box-shadow

  &__title
    font-size: 1.75rem
  &__subtitle
    font-size: 1.25rem
    font-weight: 300
  &__date
    padding-top: $base-padding
    font-size: $font-size--small
    font-style: italic
    opacity: .75


.load-more
  display: flex
  flex-flow: column nowrap
  align-items: center
  padding: $base-padding*2 $base-padding

  &__page-details
    font-size: $font-size--small
    color: $accent--dark

  &__progress
    width: 25%
    margin: 0

  &__load-more-button
    width: 25%
    @media screen and (max-width: 48rem)
      width: 100%

  &__go-to-top
    font-size: $font-size--small
    color: $accent--dark


</style>
